import { useMutation } from "@apollo/client/react";
import {
  Alert,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  styled,
  styled as styledMui,
  useTheme,
} from "@mui/material";
import { ChangeEvent, FC } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Permissions from "../../constants/permissions";
import { useAccountContext } from "../../strict/app/AccountResolver";
import { ARCHIVE_TEST_MUTATION } from "../../graphql";
import { Changelist, Event, Flow } from "../../graphql/types";

import { ClockIcon } from "@phosphor-icons/react/dist/csr/Clock";
import { PlusSquareIcon } from "@phosphor-icons/react/dist/csr/PlusSquare";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { useNavigator } from "../../lib/Hooks";
import { toLocalAndUtcDates } from "../../lib/Util";
import { IfPermission } from "../Permissions";
import { RunDetails } from "../RunDetails";
import { DrawerContainer } from "../ui/common";
import { FlowProgressBar } from "../ui/FlowProgressBar";
import { IconCopyToClipboard } from "../ui/IconCopyToClipboard";
import { UserAvatarWithTooltip } from "../ui/UserAvatar";

const Link = styled("a")`
  font-weight: 600;
  font-size: 16px;

  &:hover {
    text-decoration: underline;
  }
`;
const VersionLink = ({ changelist }: { changelist: Changelist }) => {
  const nav = useNavigator();

  const onClick = () => {
    nav.goto(["scripts", "editor", changelist.id]);
  };

  return (
    <Tooltip id="tooltip" title="View Draft in Editor." placement="right" arrow>
      <Link onClick={onClick}>{changelist.description}</Link>
    </Tooltip>
  );
};

const ArchiveIcon = styledMui(TrashIcon)({
  fontSize: "30px",
});

const AddBoxRoundedIconStyled = styledMui(PlusSquareIcon)({
  fontSize: "30px",
});

const AlertHeaderTitle = styled("div")({
  fontSize: "18px",
  fontStyle: "normal",
  display: "block",
});

interface AlertHeaderMessageProps {
  name?: string;
}

const AlertHeaderMessage = styled("p")<AlertHeaderMessageProps>`
  word-break: break-word;
`;

const LatestFailureMessage = ({ test }: { test: Flow }) => {
  const events = test.events;
  const msg =
    events &&
    events.find(function (message: Event | null) {
      return message?.severity === "error";
    });
  if (msg && test.phase !== "succeeded") {
    return (
      <Alert severity="error" data-cy="alert-showing-error">
        <AlertHeaderTitle>
          {test.failed ? <Typography>Test Failed</Typography> : <Typography>Test Failing</Typography>}
        </AlertHeaderTitle>
        <AlertHeaderMessage name="alert-header-message">{msg.message}</AlertHeaderMessage>
      </Alert>
    );
  } else {
    return null;
  }
};
interface IRun {
  test: Flow;
  bigSize: boolean;
}

const TestInformation = ({ utc, test }: { utc: string; test: Flow }) => {
  const theme = useTheme();
  return (
    <>
      <Grid container spacing={1} direction="row" sx={{ alignItems: "center", marginBottom: "7px" }}>
        <ClockIcon color={theme.palette.text50.main} width="25px" height="25px" data-testid="access-time-icon" />
        <Typography sx={{ fontSize: 14, color: "text50.main", marginLeft: "10px" }}>{utc}</Typography>
      </Grid>
      <Grid container direction="row" sx={{ alignItems: "center", marginBottom: "7px" }} spacing={1}>
        <IconCopyToClipboard id={test.id} type="Flow ID" />
      </Grid>
      <Grid container spacing={1} direction="row" sx={{ alignItems: "center" }}>
        <UserAvatarWithTooltip
          user={test.launched_by}
          prefix="Launched by "
          sxIconButton={{
            marginLeft: "0",
            marginRight: "0",
            padding: "0",
          }}
        />
        <Typography sx={{ fontSize: 14, color: "text50.main", marginLeft: "10px" }}>{test.launched_by.name}</Typography>
      </Grid>
    </>
  );
};

const Run: FC<IRun> = ({ test, bigSize }) => {
  const theme = useTheme();
  const [archiveTest] = useMutation(ARCHIVE_TEST_MUTATION, {
    refetchQueries: ["test_flows"],
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { urlName } = useAccountContext();
  const id = searchParams.get("id");

  const handleFlowViewToggle = () => {
    navigate(`/app/${urlName}/scripts/tests?id=${test.id}`);
  };

  const handleCloseDrawer = () => {
    navigate(`/app/${urlName}/scripts/tests`);
  };
  const { utc } = toLocalAndUtcDates(test.created);
  return (
    <>
      <Grid
        direction="row"
        container
        sx={{ justifyContent: "space-between", alignItems: "center", minWidth: "100%", padding: "10px" }}
        onClick={() => handleFlowViewToggle()}
      >
        <Grid container sx={{ minWidth: "100%", flexDirection: "column" }}>
          <Grid sx={{ justifyContent: "flex-start" }}>
            <VersionLink changelist={test.changelist} />
          </Grid>
          <Stack sx={{ mb: 2, mt: 2 }}>
            <LatestFailureMessage test={test} />
          </Stack>
          <Grid container direction="row" sx={{ alignItems: "center" }} spacing={1}>
            <Grid size={{ xs: 4, sm: 5, lg: 3, md: 5 }}>
              <TestInformation utc={utc} test={test} />
            </Grid>
            <Grid container direction="row" sx={{ alignItems: "center" }} size={{ xs: 2, sm: 4, lg: 7, md: 5 }}>
              <FlowProgressBar
                phase={test.phase}
                failed={test.failed}
                orientation={bigSize ? "horizontal" : "vertical"}
              />
            </Grid>
            <Grid
              container
              direction="row"
              size={{ lg: 2, md: 2, xs: 2, sm: 3 }}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                marginLeft: "auto",
              }}
            >
              <IfPermission permission={Permissions.DevelopScripts}>
                <Tooltip title="Archive" placement="bottom" arrow>
                  <IconButton
                    size="large"
                    onClick={(e) => {
                      e.stopPropagation();
                      archiveTest({ variables: { id: test.id } });
                    }}
                    data-cy="archive-button"
                  >
                    <ArchiveIcon />
                  </IconButton>
                </Tooltip>
              </IfPermission>
              <Tooltip title="View details" placement="bottom" arrow>
                <IconButton size="large" data-cy="view-details-button" onClick={handleFlowViewToggle}>
                  <AddBoxRoundedIconStyled />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Drawer
        anchor="right"
        open={id === test.id}
        onClose={(event: ChangeEvent) => {
          event.stopPropagation();
          handleCloseDrawer();
        }}
        data-cy="drawer"
      >
        <DrawerContainer sx={{ overflowY: "auto" }}>
          <Stack sx={{ alignItems: "center", justifyContent: "flex-start", minWidth: "100%" }} spacing={1}>
            <Stack direction="row" sx={{ justifyContent: "center", marginTop: "10px", textAlign: "center" }}>
              <VersionLink changelist={test.changelist} />
            </Stack>
            <Stack spacing={1} direction="row" sx={{ alignItems: "center", marginTop: "10px" }}>
              <ClockIcon color={theme.palette.surfaceLight50.main} />
              <Typography sx={{ fontSize: 14, color: "text50.main" }}>{utc}</Typography>
            </Stack>
            <Stack direction="row" sx={{ justifyContent: "center", alignItems: "center" }} spacing={1}>
              <IconCopyToClipboard id={test.id} type="Flow ID" />
            </Stack>
            <Stack spacing={1} direction="row" sx={{ alignItems: "center", marginTop: "10px" }}>
              <UserAvatarWithTooltip
                user={test.launched_by}
                prefix="Launched by "
                sxIconButton={{
                  marginLeft: "0",
                  marginRight: "0",
                  padding: "0",
                }}
              />
              <Typography sx={{ fontSize: 14, color: "text50.main" }}>{test.launched_by.name}</Typography>
            </Stack>
            <Stack spacing={1} sx={{ width: "70%", justifyContent: "center" }} direction="row">
              <FlowProgressBar
                phase={test.phase}
                failed={test.failed}
                orientation={bigSize ? "horizontal" : "vertical"}
              />
            </Stack>
            <RunDetails run={test} />
          </Stack>
        </DrawerContainer>
      </Drawer>
    </>
  );
};

export { LatestFailureMessage, Run };
